/**
 * 太陽光の CSM 影マップと、空・太陽の定数。
 * 直射の色は ReSTIR シェード側で評価し、ここはカスケード影の生成だけを担う。
 */

import * as THREE from 'three';
import { CSM } from 'three/addons/csm/CSM.js';
import type { GraphicsQuality } from './graphics-quality';
import { GRAPHICS_PRESETS, DEFAULT_GRAPHICS_QUALITY, GROUND_DRAW_DISTANCE } from './graphics-quality';

/** 太陽が照らす方向（ライトからシーンへ向かう単位ベクトル） */
export const SUN_DIRECTION = new THREE.Vector3(0.42, -0.82, 0.38).normalize();
export const SUN_COLOR = new THREE.Color(0xfff1c8);
export const SUN_INTENSITY = 2.2;

/** h は 0–360、s/v は 0–1。カラーピッカーと同じ sRGB の HSV。 */
function hsv(h: number, s: number, v: number): THREE.Color {
  const hh = (((h % 360) + 360) % 360) / 60;
  const c = v * s;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  const m = v - c;
  const rgb =
    hh < 1 ? [c, x, 0]
    : hh < 2 ? [x, c, 0]
    : hh < 3 ? [0, c, x]
    : hh < 4 ? [0, x, c]
    : hh < 5 ? [x, 0, c]
    : [c, 0, x];
  return new THREE.Color().setRGB(rgb[0] + m, rgb[1] + m, rgb[2] + m, THREE.SRGBColorSpace);
}

export const SKY_ZENITH = hsv(202, 0.81, 0.89);
export const SKY_HORIZON = hsv(202, 0.34, 0.96);
export const SKY_GROUND = hsv(82, 0.29, 0.6);

/** CSM ライトは常に最大段数分用意し、品質に応じて有効数だけ影を落とす */
export const CSM_CASCADE_CAPACITY = 3;

/**
 * Standard マテリアルへ CSM チャンクを差し込まない。
 * 太陽光の可視性は ReSTIR の合成パスで shadow map を直接サンプルする。
 */
class ShadowOnlyCsm extends CSM {
  injectInclude(): void {}
}

export class Environment {
  readonly csm: CSM;
  private readonly renderer: THREE.WebGLRenderer;
  private quality: GraphicsQuality;
  private _sunEnabled = true;

  constructor(
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
    renderer: THREE.WebGLRenderer,
    initialQuality: GraphicsQuality = GRAPHICS_PRESETS[DEFAULT_GRAPHICS_QUALITY]
  ) {
    this.renderer = renderer;
    this.quality = initialQuality;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.shadowMap.autoUpdate = false;
    scene.background = null;
    scene.fog = null;

    this.csm = new ShadowOnlyCsm({
      camera,
      parent: scene,
      cascades: CSM_CASCADE_CAPACITY,
      maxFar: GROUND_DRAW_DISTANCE,
      mode: 'practical',
      shadowMapSize: initialQuality.shadowMapSize,
      lightDirection: SUN_DIRECTION.clone(),
      lightIntensity: SUN_INTENSITY,
      shadowBias: -0.0002,
      lightNear: 0.5,
      lightFar: 2500,
      lightMargin: 80,
    });
    this.csm.fade = true;
    this.applyQuality(initialQuality);
  }

  get sunEnabled(): boolean {
    return this._sunEnabled;
  }

  set sunEnabled(value: boolean) {
    if (this._sunEnabled === value) return;
    this._sunEnabled = value;
    this.applyLights();
  }

  /** このフレームで太陽光の shadow map を焼き直すか。 */
  get shouldUpdateShadows(): boolean {
    return this._sunEnabled && this.quality.shadows;
  }

  get activeShadowCascades(): number {
    if (!this.shouldUpdateShadows) return 0;
    return this.quality.shadowCascadesActive;
  }

  applyQuality(quality: GraphicsQuality): void {
    this.quality = quality;
    this.renderer.shadowMap.type = quality.softShadows
      ? THREE.PCFSoftShadowMap
      : THREE.BasicShadowMap;

    this.csm.maxFar = GROUND_DRAW_DISTANCE;
    this.csm.shadowMapSize = quality.shadowMapSize;
    this.applyLights();
    this.csm.updateFrustums();
  }

  updateFrustums(): void {
    this.csm.updateFrustums();
  }

  /** カメラ追従の CSM 更新 */
  update(_elapsed: number, camera: THREE.Camera): void {
    camera.updateMatrixWorld();
    this.csm.update();
    if (this.shouldUpdateShadows) this.renderer.shadowMap.needsUpdate = true;
  }

  dispose(): void {
    this.csm.remove();
    this.csm.dispose();
  }

  private applyLights(): void {
    const shadows = this.shouldUpdateShadows;
    this.renderer.shadowMap.enabled = shadows;
    this.renderer.shadowMap.needsUpdate = shadows;

    const activeCascades = this.activeShadowCascades;
    const size = this.quality.shadowMapSize;
    for (let i = 0; i < this.csm.lights.length; i++) {
      const light = this.csm.lights[i];
      const casts = i < activeCascades;
      light.castShadow = casts;
      light.visible = casts;
      light.intensity = 0;
      if (!casts) continue;
      if (light.shadow.mapSize.x !== size || light.shadow.mapSize.y !== size) {
        light.shadow.mapSize.set(size, size);
        if (light.shadow.map) {
          light.shadow.map.dispose();
          light.shadow.map = null as unknown as THREE.WebGLRenderTarget;
        }
      }
      light.shadow.normalBias = 0.035;
      light.shadow.radius = this.quality.softShadows ? 1.5 : 1;
    }
  }
}
